import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import { Box, Stack } from '@mui/material';
import {
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  FOCUS_COMMAND,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useState } from 'react';

import { Bold01Icon } from '@/components/icons/Bold01Icon';
import { Italic01Icon } from '@/components/icons/Italic01Icon';
import { Strikethrough01Icon } from '@/components/icons/Strikethrough01Icon';
import { TextEditorToolbarButton } from '@/components/TextEditor/plugins/ToolbarPlugin/ToolbarPlugin';
import { COLORS } from '@/styles/tokens/colors';

const iconProps = {
  size: 20,
};

const LowPriority = 1;

/**
 * Simplified toolbar that only shows inline text formatting options
 * (bold, italic, strikethrough) without the block type selector.
 */
export function SimpleTextToolbar() {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload) => {
          updateToolbar();
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        FOCUS_COMMAND,
        () => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setSelection(selection.clone());
          }
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  return (
    <Stack
      p={1}
      bgcolor={COLORS.GRAY[100]}
      direction="row"
      justifyContent="flex-start"
      spacing={0.5}
      sx={{
        borderRadius: 1,
        borderBottom: `1px solid ${COLORS.GRAY[200]}`,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      }}
    >
      <TextEditorToolbarButton
        isActive={isBold}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
      >
        <Bold01Icon {...iconProps} />
      </TextEditorToolbarButton>
      <TextEditorToolbarButton
        isActive={isItalic}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        }}
      >
        <Italic01Icon {...iconProps} />
      </TextEditorToolbarButton>
      <TextEditorToolbarButton
        isActive={isStrikethrough}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
        }}
      >
        <Strikethrough01Icon {...iconProps} />
      </TextEditorToolbarButton>
      <Box />
    </Stack>
  );
}
